import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/admin/sidebar/Sidebar";
import { ToastContainer } from "react-toastify";
import useDash from "../../assets/css/dash.module.css";
import { FontColor, FourGround } from "../../assets/js/theme";
import { useTranslation } from "react-i18next";
import Loader from "../../components/common/Loader";
import {
  ArchiveFill,
  ArrowClockwise,
  Diagram3Fill,
  PenFill,
  Plus,
  ThreeDots,
} from "react-bootstrap-icons";
import Table from "../../components/table/Table";
import Footer from "../../components/admin/footer/Footer";
import NetworkDialog from "./NetworkDialog";
import useISPowner from "../../hooks/useISPOwner";
import useSelectorState from "../../hooks/useSelectorState";
import {
  getArea,
  getCustomer,
  getNetworkDevice,
} from "../../features/apiCalls";
import {
  getPoleBoxApi,
  getSubAreasApi,
} from "../../features/actions/customerApiCall";
import { getBulletinPermission } from "../../features/apiCallAdmin";
import { useDispatch, useSelector } from "react-redux";
import { deletedNetworkPoint, getNetworkPoint } from "../../features/oltApi";
import { Link } from "react-router-dom";

const Network = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //---> Get user & current user data form useISPOwner hooks
  const { ispOwnerId } = useISPowner();

  //---> Get redux store state data from useSelectorState hooks
  const { areas, subAreas, polesBox, bulletinPermission } = useSelectorState();

  //---> Get user network data from redux store
  const networks = useSelector((state) => state.network?.network);

  //---> Local State
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalStatus, setModalStatus] = useState("");
  const [isUpdate, setIsUpdate] = useState(false);
  const [show, setShow] = useState(false);
  const [networkPoint, setNetworkPoint] = useState([]);

  //================// API CALL's //================//
  useEffect(() => {
    //===========================================================> FIRST API
    //---> @Get user network points data
    !networks?.length && getNetworkPoint(dispatch, ispOwnerId, setIsLoading);

    getNetworkDevice(dispatch, ispOwnerId, setLoading);

    //---> @Get ispOwner areas data
    !areas?.length && getArea(dispatch, ispOwnerId, setLoading);

    //---> @Get ispOwner areas sub-area data
    !subAreas.length && getSubAreasApi(dispatch, ispOwnerId);

    //---> @Get ispOwner all customer data
    getCustomer(dispatch, ispOwnerId, setLoading);

    //===========================================================> LAST API
    //---> @Get ispOwner sub-areas pol-box data
    !polesBox?.length && getPoleBoxApi(dispatch, ispOwnerId, setLoading);

    //---> @Get bulletin permissions data
    Object.keys(bulletinPermission)?.length === 0 &&
      getBulletinPermission(dispatch);
  }, [ispOwnerId]);

  const networkReloadHandler = () => {
    getNetworkPoint(dispatch, ispOwnerId, setIsLoading);
  };

  const handleDeleteNetworkPoint = (data) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this point?"
    );

    if (confirmDelete) {
      deletedNetworkPoint(dispatch, data.ispOwner, data._id, setLoading);
    }
  };

  const columns = useMemo(
    () => [
      {
        width: "5%",
        Header: t("serial"),
        id: "row",
        accessor: (row) => Number(row.id + 1),
        Cell: ({ row }) => <strong>{Number(row.id) + 1}</strong>,
      },
      {
        width: "15%",
        Header: t("name"),
        accessor: "name",
      },
      {
        width: "10%",
        Header: t("area"),
        accessor: "area.name",
      },
      {
        width: "10%",
        Header: t("subArea"),
        accessor: "subArea.name",
      },
      {
        width: "20%",
        Header: t("description"),
        accessor: "description",
      },
      {
        width: "6%",
        Header: t("diagram"),
        Cell: ({ row: { original } }) => (
          <div className="d-flex align-items-center gap-2">
            <Link
              to={`/network/diagram/${original?._id}`}
              type="button"
              className="btn btn-sm btn-primary d-flex justify-content-center align-items-center text-white gap-1"
            >
              <Diagram3Fill size={18} />
            </Link>
          </div>
        ),
      },
      {
        width: "10%",
        Header: () => <div className="text-center">{t("action")}</div>,
        id: "option",
        Cell: ({ row: { original } }) => (
          <div className="d-flex justify-content-center align-items-center">
            <ThreeDots
              className="dropdown-toggle ActionDots"
              id="areaDropdown"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            />
            <ul className="dropdown-menu" aria-labelledby="customerDrop">
              <li
                onClick={() => {
                  setModalStatus("network");
                  setIsUpdate(true);
                  setNetworkPoint(original);
                  setShow(true);
                }}
              >
                <div className="dropdown-item actionManager">
                  <div className="customerAction">
                    <PenFill />
                    <p className="actionP">{t("edit")}</p>
                  </div>
                </div>
              </li>

              <li onClick={() => handleDeleteNetworkPoint(original)}>
                <div className="dropdown-item actionManager">
                  <div className="customerAction">
                    <ArchiveFill />
                    <p className="actionP">{t("delete")}</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        ),
      },
    ],
    [t]
  );

  return (
    <>
      <Sidebar />
      <ToastContainer position="top-right" theme="colored" />
      <div className={useDash.dashboardWrapper}>
        <div className="container-fluied collector">
          <div className="container">
            <FontColor>
              <FourGround>
                <div className="collectorTitle d-flex justify-content-between px-4">
                  <div className="component_name">{t("network")}</div>

                  <div className="d-flex justify-content-center align-items-center">
                    <div className="reloadBtn">
                      {isLoading ? (
                        <Loader />
                      ) : (
                        <ArrowClockwise
                          className="arrowClock"
                          title={t("refresh")}
                          onClick={networkReloadHandler}
                        />
                      )}
                    </div>

                    <div
                      onClick={() => {
                        setModalStatus("network");
                        setIsUpdate(false);
                        setShow(true);
                      }}
                    >
                      <Plus className="addcutmButton" />
                    </div>
                  </div>
                </div>
              </FourGround>
              <FourGround>
                <div className="collectorWrapper mt-2 py-2">
                  <Table
                    isLoading={isLoading}
                    columns={columns}
                    data={networks}
                  />
                </div>
              </FourGround>
              <Footer />
            </FontColor>
          </div>
        </div>
      </div>

      {/* Network Dialog */}
      {modalStatus === "network" && (
        <NetworkDialog {...{ show, setShow, isUpdate, networkPoint }} />
      )}
    </>
  );
};

export default Network;
